import { currencies, Currency } from '~/constants/currency';
import { DistanceUnit, distanceUnitMap } from '~/constants/distance';
import { WeightUnit, weightUnitMap } from '~/constants/weight';
import * as moment from 'moment';
import APIAddress, * as apiAddress from '~/types/APIAddress';
import LabelFormat from '~/types/LabelFormat';
import ParcelTemplate, * as parcelTemplate from '~/types/ParcelTemplate';
import Promo, * as promo from '~/types/Promo';

type JSONUser = {
  id: number;
  login_user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  address: apiAddress.JSONAPIAddress;
  address_return: apiAddress.JSONAPIAddress;
  default_parcel: parcelTemplate.JSONParcelTemplate;
  default_distance_unit: string;
  default_mass_unit: string;
  default_outbound_account: Maybe<string>;
  default_provider: Maybe<string>;
  default_service_level: Maybe<string>;
  default_return_account: Maybe<string>;
  default_return_provider: Maybe<string>;
  default_return_service_level: Maybe<string>;
  default_label_filetype: Maybe<string>;
  default_auto_create_return_label: boolean;
  currency_iso: string;
  revenue_tier_name: string;
  insurance_rate: number;
  is_active: boolean;
  blocked_billing: boolean;
  fraud_status: string; // This is fraud_status_id. Intentionally vaguely named to stop attackers from tampering.
  group_name: string;
  has_paid_invoice: boolean;
  stripe_authorized: boolean;
  stripe_publishable_key: string;
  payment_type: string;
  current_promo: Maybe<promo.JSONPromo>;
  is_setup: boolean;
  date_joined: string;
  lifetime_vol_segment_min: number;
  lifetime_vol_segment_max: number;
  savings_monthly_amt: string;
  savings_carrier_name: string;
  has_purchased_label: boolean;
  is_idv_required: boolean;
  group_jwt: string;
  is_account_owner: boolean;
  object_id: string;
  requires_email_verification: Maybe<boolean>;
  has_failed_payment: boolean;
  insurance_rate_domestic?: number;
  insurance_rate_international?: number;
};

type User = {
  id: number;
  loginUserId: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  address: Maybe<APIAddress>;
  addressReturn: Maybe<APIAddress>;
  defaultParcel: Maybe<ParcelTemplate>;
  defaultDistanceUnit: DistanceUnit;
  defaultMassUnit: WeightUnit;
  defaultOutboundAccount: Maybe<string>;
  defaultProvider: Maybe<string>;
  defaultServiceLevel: Maybe<string>;
  defaultReturnProvider: Maybe<string>;
  defaultReturnServiceLevel: Maybe<string>;
  defaultLabelFiletype: Maybe<LabelFormat>;
  defaultAutoCreateReturnLabel: boolean;
  currencyIso: Currency;
  revenueTierName: string;
  isActive: boolean;
  blockedBilling: boolean;
  fraudStatus: string;
  groupName: string; // This is fraud_status_id. Intentionally vaguely named to stop attackers from tampering.
  hasPaidInvoice: boolean;
  stripeAuthorized: boolean;
  stripePublishableKey: string;
  paymentType: string;
  currentPromo: Maybe<Promo>;
  isSetup: boolean;
  dateJoined: moment.Moment;
  lifetimeVolSegmentMin: number;
  lifetimeVolSegmentMax: number;
  savingsMonthlyAmount: number;
  savingsCarrierName: string;
  hasPurchasedLabel: boolean;
  isIDVRequired: boolean;
  default_outbound_account?: string | null;
  defaultReturnAccount?: string | null;
  isAccountOwner: boolean;
  requiresEmailVerification: boolean;
  insuranceRate: number;
  objectId: string;
  hasFailedPayment: boolean;
  insuranceRateDomestic?: number;
  insuranceRateInternational?: number;
};

export type UserConfigDefaults = {
  defaultParcel: ParcelTemplate;
  defaultDistanceUnit: DistanceUnit;
  defaultMassUnit: WeightUnit;
  defaultCurrency: Currency;
  defaultAutoCreateReturnLabel: boolean;
};

type UserDefaultServiceLevel = {
  provider: string;
  token: string;
  accountId: Maybe<string | undefined>;
};

type UserDefaultService = { provider: string; token: string };

function getFullName(user: User) {
  return `${user.firstName} ${user.lastName}`;
}

// TODO: We should refactor this function so that the call site
// can handle billing issue better, ie is the user billing blocked vs
// no stripe authorize card on file
function hasValidBilling(user: User) {
  if (user.blockedBilling || !user.isActive) {
    return false;
  }
  return user.stripeAuthorized || (user.paymentType !== null && user.paymentType !== '');
}

function isHighFraudRisk(user: User) {
  return user.fraudStatus === 'HIGH_RISK';
}

function fromJSON(jsonUser: JSONUser): User {
  return {
    id: jsonUser.id,
    loginUserId: jsonUser.login_user_id,
    username: jsonUser.username,
    firstName: jsonUser.first_name,
    lastName: jsonUser.last_name,
    email: jsonUser.email,
    address: apiAddress.fromJSON(jsonUser.address),
    addressReturn: apiAddress.fromJSON(jsonUser.address_return),
    defaultParcel: jsonUser.default_parcel
      ? parcelTemplate.fromJSON(jsonUser.default_parcel)
      : null,
    defaultDistanceUnit: distanceUnitMap[jsonUser.default_distance_unit] || DistanceUnit.IN,
    defaultMassUnit: weightUnitMap[jsonUser.default_mass_unit] || WeightUnit.LB,
    defaultOutboundAccount: jsonUser.default_outbound_account,
    defaultProvider: jsonUser.default_provider,
    defaultServiceLevel: jsonUser.default_service_level,
    defaultReturnAccount: jsonUser.default_return_account,
    defaultReturnProvider: jsonUser.default_return_provider,
    defaultReturnServiceLevel: jsonUser.default_return_service_level,
    defaultLabelFiletype: <LabelFormat>jsonUser.default_label_filetype,
    defaultAutoCreateReturnLabel: jsonUser.default_auto_create_return_label,
    currencyIso: currencies[jsonUser.currency_iso.toUpperCase()] || currencies.USD,
    revenueTierName: jsonUser.revenue_tier_name || '5',
    isActive: jsonUser.is_active,
    blockedBilling: jsonUser.blocked_billing,
    fraudStatus: jsonUser.fraud_status,
    groupName: jsonUser.group_name,
    hasPaidInvoice: jsonUser.has_paid_invoice,
    stripeAuthorized: jsonUser.stripe_authorized,
    stripePublishableKey: jsonUser.stripe_publishable_key,
    paymentType: jsonUser.payment_type,
    currentPromo: promo.promoFromJSON(jsonUser.current_promo),
    isSetup: jsonUser.is_setup,
    dateJoined: moment(jsonUser.date_joined),
    lifetimeVolSegmentMin: jsonUser.lifetime_vol_segment_min,
    lifetimeVolSegmentMax: jsonUser.lifetime_vol_segment_max,
    savingsMonthlyAmount: parseFloat(jsonUser.savings_monthly_amt),
    savingsCarrierName: jsonUser.savings_carrier_name,
    hasPurchasedLabel: jsonUser.has_purchased_label,
    isIDVRequired: !!jsonUser.is_idv_required,
    isAccountOwner: jsonUser.is_account_owner,
    requiresEmailVerification: !!jsonUser.requires_email_verification,
    insuranceRate: jsonUser.insurance_rate,
    objectId: jsonUser.object_id,
    hasFailedPayment: jsonUser.has_failed_payment || false,
    insuranceRateDomestic: jsonUser.insurance_rate_domestic,
    insuranceRateInternational: jsonUser.insurance_rate_international,
  };
}

type ProfileUser = {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  password: string;
  newPasswordConfirmation: string;
  newPassword: string;
  isAccountOwner: boolean;
  loginUserId: number;
};

type LoginUser = {
  firstName: string;
  lastName: string;
  email: string;
  id: number;
  isActive: boolean;
  isEmailRecipient: boolean;
};

export default User;
export {
  ProfileUser,
  User,
  JSONUser,
  UserDefaultService,
  getFullName,
  hasValidBilling,
  fromJSON,
  UserDefaultServiceLevel,
  isHighFraudRisk,
  LoginUser,
};
